import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { translations } from './Translation';


const generateGoogleCalendarLink = (app) => {
  const start = new Date(`${app.date}T${app.time}`);
  const end = new Date(start.getTime() + parseDuration(app.duration) * 60000);
  const formatTime = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, '');
  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`${app.serviceName} with ${app.barberName}`)}&dates=${formatTime(start)}/${formatTime(end)}&details=${encodeURIComponent(`Haircut at ${app.shopName}`)}&location=${encodeURIComponent(app.shopName)}`;
  window.open(url, '_blank');
};

const parseDuration = (durStr) => {
  if (!durStr) return 30; 
  let mins = 0;
  const hMatch = durStr.match(/(\d+)\s*h/i);
  const mMatch = durStr.match(/(\d+)\s*m/i);
  if (hMatch) mins += parseInt(hMatch[1]) * 60;
  if (mMatch) mins += parseInt(mMatch[1]);
  return mins || 30;
};
const timeToMins = (timeStr) => { if (!timeStr) return 0; const [h, m] = timeStr.split(':').map(Number); return h * 60 + m; };
const minsToTime = (mins) => { const h = Math.floor(mins / 60).toString().padStart(2, '0'); const m = (mins % 60).toString().padStart(2, '0'); return `${h}:${m}`; };



function ClientDashboard({ user, onNavigate }) {
  

  const [activeTab, setActiveTab] = useState('bookings');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientLocation, setClientLocation] = useState(user.location || '');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [rescheduleApp, setRescheduleApp] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '' });
  const [shopSchedule, setShopSchedule] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedIntervals, setBookedIntervals] = useState([]); 

  const [language, setLanguage] = useState(localStorage.getItem('elitecut_lang') || 'en');
  const t = translations[language] || translations.en;
  

  useEffect(() => {
    const handleStorage = () => setLanguage(localStorage.getItem('elitecut_lang') || 'en');
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!user) return;
      try {
        const q = query(collection(db, "appointments"), where("clientId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const appsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        appsList.sort((a, b) => new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time));
        setAppointments(appsList);
      } catch (error) { console.error(error); } 
      finally { setLoading(false); }
    };
    fetchAppointments();
  }, [user]);

  const openRescheduleModal = async (app) => {
    setRescheduleApp(app);
    setRescheduleData({ date: app.date, time: '' });
    const shopSnap = await getDoc(doc(db, "shops", app.shopId));
    if (shopSnap.exists()) setShopSchedule(shopSnap.data().schedule);
  };

  useEffect(() => {
    const fetchBookingsForReschedule = async () => {
      if (!rescheduleApp || !rescheduleData.date) return;
      const q = query(collection(db, "appointments"), where("shopId", "==", rescheduleApp.shopId), where("barberName", "==", rescheduleApp.barberName), where("date", "==", rescheduleData.date));
      const snapshot = await getDocs(q);
      const intervals = [];
      snapshot.docs.forEach(doc => {
        if (doc.id !== rescheduleApp.id) {
          const data = doc.data();
          const startMins = timeToMins(data.time);
          intervals.push({ start: startMins, end: startMins + parseDuration(data.duration) });
        }
      });
      setBookedIntervals(intervals);
    };
    fetchBookingsForReschedule();
  }, [rescheduleData.date, rescheduleApp]);

  useEffect(() => {
    if (!rescheduleData.date || !shopSchedule || !rescheduleApp) return;
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todaySchedule = shopSchedule[days[new Date(rescheduleData.date).getDay()]];
    if (!todaySchedule || todaySchedule.closed) { setAvailableSlots([]); return; }

    const serviceDuration = parseDuration(rescheduleApp.duration);
    const openMins = timeToMins(todaySchedule.open);
    const closeMins = timeToMins(todaySchedule.close);
    
    const slots = [];
    let currentMins = openMins;
    while (currentMins + serviceDuration <= closeMins) {
      const proposedStart = currentMins;
      const proposedEnd = currentMins + serviceDuration;
      const hasOverlap = bookedIntervals.some(b => (proposedStart < b.end && proposedEnd > b.start));
      if (!hasOverlap) slots.push(minsToTime(proposedStart));
      currentMins += 15;
    }
    setAvailableSlots(slots);
  }, [rescheduleData.date, shopSchedule, bookedIntervals, rescheduleApp]);

  const submitReschedule = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, "appointments", rescheduleApp.id), { date: rescheduleData.date, time: rescheduleData.time });
      setAppointments(appointments.map(app => app.id === rescheduleApp.id ? { ...app, date: rescheduleData.date, time: rescheduleData.time } : app));
      setRescheduleApp(null);
      alert("Rescheduled successfully!");
    } catch (error) { alert("Error rescheduling: " + error.message); }
  };

  const handleCancel = async (appId) => {
    if (window.confirm("Are you sure you want to cancel this appointment?")) {
      try {
        await deleteDoc(doc(db, "appointments", appId));
        setAppointments(appointments.filter(app => app.id !== appId));
      } catch (error) { alert("Error canceling: " + error.message); }
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setIsSavingSettings(true);
    try {
      await updateDoc(doc(db, "users", user.uid), { location: clientLocation });
      alert("Settings saved!");
    } catch (error) { alert("Error saving settings."); }
    setIsSavingSettings(false);
  };

  return (
    <div className="dashboard-container">
      <aside className="dash-sidebar client-sidebar">
        <div className="dash-logo" onClick={() => onNavigate('home')} style={{ cursor: 'pointer', transition: '0.2s' }}>
          {t.backHome}
        </div>
        <nav>
          <button className={activeTab === 'bookings' ? 'active' : ''} onClick={() => setActiveTab('bookings')}>{t.myBookings}</button>
          <button className={activeTab === 'favorites' ? 'active' : ''} onClick={() => setActiveTab('favorites')}>{t.favShops}</button>
          <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>{t.settings}</button>
          <button onClick={() => auth.signOut()} style={{marginTop: 'auto', color: '#ef4444'}}>{t.logout}</button>
        </nav>
      </aside>

      <main className="dash-content">
        <header className="dash-header">
          <div>
            <h1>{t.hello}, {user.name}</h1>
            <p>{t.welcomeDash}</p>
          </div>
          <button className="premium-book-btn" onClick={() => onNavigate('home')}>{t.findBarber}</button>
        </header>

        {activeTab === 'bookings' && (
          <div className="admin-section">
            <h2>{t.upcApps}</h2>
            {loading ? <p>Loading...</p> : appointments.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px 0'}}>
                <p style={{color: '#64748b', marginBottom: '20px'}}>{t.noApps}</p>
                <button className="premium-login-btn" onClick={() => onNavigate('home')}>{t.bookFirst}</button>
              </div>
            ) : (
              appointments.map(app => (
                <div key={app.id} className="appointment-card" style={{marginBottom: '15px'}}>
                  <div className="app-time" style={{width: '120px'}}>
                    <h3>{app.date}</h3>
                    <p>{app.time}</p>
                  </div>
                  <div className="app-details" style={{flex: 1, paddingLeft: '20px', borderLeft: '2px solid #f1f5f9'}}>
                    <h4>{app.serviceName} w/ {app.barberName}</h4>
                    <p>📍 {app.shopName} — <strong>{app.price}</strong></p>
                  </div>
                  <div className="app-actions" style={{display: 'flex', gap: '10px'}}>
                    <button className="outline-btn danger" onClick={() => handleCancel(app.id)}>{t.cancel}</button>
                    <button className="premium-login-btn" onClick={() => openRescheduleModal(app)}>{t.reschedule}</button>
                    <button className="outline-btn" onClick={() => generateGoogleCalendarLink(app)} style={{ borderColor: '#10b981', color: '#10b981', padding: '10px', borderRadius: '10px', background: 'transparent' }}>
                        {t.addCal}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="admin-section">
            <h2>{t.favShops}</h2>
            <p className="empty-text">{t.noFavs}</p>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="admin-section">
            <h2>{t.accSettings}</h2>
            <form onSubmit={saveSettings} style={{maxWidth: '400px'}}>
              <div className="input-group" style={{marginBottom: '15px'}}><label>{t.fullName}</label><input type="text" defaultValue={user.name} disabled style={{background: '#f1f5f9'}} /></div>
              <div className="input-group" style={{marginBottom: '15px'}}><label>{t.emailAddr}</label><input type="email" defaultValue={user.email} disabled style={{background: '#f1f5f9'}} /></div>
              <div className="input-group" style={{marginBottom: '20px'}}><label>{t.cityLoc}</label><input type="text" value={clientLocation} onChange={(e) => setClientLocation(e.target.value)} style={{padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px'}} /></div>
              <button type="submit" className="premium-book-btn" disabled={isSavingSettings}>{isSavingSettings ? t.saving : t.saveSet}</button>
            </form>
          </div>
        )}
      </main>

      {rescheduleApp && (
        <div className="login-modal-overlay" onClick={() => setRescheduleApp(null)}>
          <div className="login-modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '400px'}}>
            <button className="modal-close-btn" onClick={() => setRescheduleApp(null)}>✕</button>
            <div className="form-header">
              <h2>{t.reschedule}</h2>
              <p>{t.rescheduleSub} {rescheduleApp.barberName}.</p>
            </div>
            
            <form className="auth-form" onSubmit={submitReschedule}>
              <div className="input-group">
                <label>{t.newDate}</label>
                <input type="date" value={rescheduleData.date} onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value, time: ''})} required />
              </div>

              {rescheduleData.date && (
                <div className="input-group">
                  <label>{t.availTime}</label>
                  <select value={rescheduleData.time} onChange={(e) => setRescheduleData({...rescheduleData, time: e.target.value})} required style={{padding: '12px', borderRadius: '8px'}}>
                    <option value="" disabled>{t.selectTime}</option>
                    {availableSlots.length === 0 ? <option value="" disabled>{t.noSlots}</option> : availableSlots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
                  </select>
                </div>
              )}
              <button type="submit" className="submit-auth-btn client">{t.confReschedule}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDashboard;